'use client'

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  type ReactNode,
} from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { clientRequest } from 'gpApi/typed-request'
import { Eligibility, Organization } from 'gpApi/api-endpoints'
import type { OrganizationRole } from '@goodparty_org/contracts'
import { getCookie, setCookie } from 'helpers/cookieHelper'
import { EVENTS, trackEvent } from 'helpers/analyticsHelper'
import { ORG_SLUG_COOKIE } from '@shared/organizations/constants'
import { useSelectedOrgSlug } from '@shared/hooks/useSelectedOrgSlug'
import { useFeatureFlags } from '@shared/experiments/FeatureFlagsProvider'
import { outreachDetailQueryPrefix } from 'app/dashboard/outreach/v2/useOutreachDetail'
import {
  DropdownMenu,
  DropdownMenuItem,
  DropdownMenuGroup,
  DropdownMenuContent,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  ProBadge,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  ArrowRightIcon,
} from '@styleguide'
import { ChevronDown } from 'lucide-react'
import { useIsMobile } from '@styleguide/hooks/use-mobile'
import { usePathname, useRouter } from 'next/navigation'
import { useCampaign } from './hooks/useCampaign'

const SHARED_PATHS = [
  '/dashboard/profile',
  '/dashboard/campaign-details',
  '/dashboard/account',
  // Org-scoped but valid under any org: the team query keys on the org slug
  // and refetches on switch, so stay put instead of bouncing to /dashboard.
  '/dashboard/team',
]

interface OrganizationContextValue {
  organizations: Organization[]
  selected: Organization | undefined
  setSelectedSlug: (slug: string) => void
}

const OrganizationContext = createContext<OrganizationContextValue | null>(null)

export const useOrganization = (): Organization | undefined => {
  const ctx = useContext(OrganizationContext)
  if (!ctx) {
    throw new Error('useOrganization must be used within OrganizationProvider')
  }
  return ctx.selected
}

// The full list, for surfaces that build their own switcher UI instead of
// rendering `OrganizationPicker` (e.g. the volunteer sidebar's in-sidebar
// "switch campaign" list, ENG-11068).
export const useOrganizations = (): Organization[] => {
  const ctx = useContext(OrganizationContext)
  if (!ctx) {
    throw new Error('useOrganizations must be used within OrganizationProvider')
  }
  return ctx.organizations
}

// The viewer's own role in the selected org (owner via ownerId match, else
// their membership role — see gp-api's OrganizationMembershipService). Only
// GET /v1/organizations (the list this provider is seeded from) sends role,
// so it's undefined only when there's no selected org at all (e.g. signed in
// with no organizations yet) — never for a resolved org, where the server
// always answers 'owner' for a solo user.
export const useOrganizationRole = (): OrganizationRole | undefined =>
  useOrganization()?.role

export const useSetOrganizationSlug = () => {
  const ctx = useContext(OrganizationContext)
  if (!ctx) {
    throw new Error(
      'useSetOrganizationSlug must be used within OrganizationProvider',
    )
  }
  return ctx.setSelectedSlug
}

interface OrganizationProviderProps {
  children: ReactNode
  initialOrganizations: Organization[]
  initialSlug?: string | null
}

export const ORGANIZATIONS_QUERY_KEY = ['organizations']
// Eligibility is per-user, not per-org, so it must survive an org switch
// untouched (see the invalidation predicate in setSelectedSlug).
export const ELIGIBILITY_QUERY_KEY = ['eligibility']
// The person-notes family (NotesSection, PhoneBankingNotes) — per-person-id
// rows, excluded from the org-switch invalidation for the same reason as
// outreach-detail (see setSelectedSlug).
const CONTACT_NOTES_QUERY_PREFIX = 'contact-notes'

export const OrganizationProvider = ({
  children,
  initialOrganizations,
  initialSlug = null,
}: OrganizationProviderProps) => {
  const queryClient = useQueryClient()
  const router = useRouter()
  // Some flags roll out by org (gp-api's ORGANIZATION_FLAGS, e.g.
  // issue-capture), resolved for the org the cookie names. The flag
  // provider sits above this one and never sees the cookie change, so
  // every change of org here asks it to resolve again.
  const { refresh: refreshFlags } = useFeatureFlags()

  const { data: organizations } = useQuery({
    queryKey: ORGANIZATIONS_QUERY_KEY,
    queryFn: async () =>
      clientRequest('GET /v1/organizations', {}).then(
        (res) => res.data.organizations,
      ),
    initialData: initialOrganizations,
  })

  const [selectedSlug, setRawSelectedSlug] = useSelectedOrgSlug(
    initialOrganizations,
    initialSlug,
  )

  const selectedOrganization = useMemo(
    () =>
      organizations.find((o) => o.slug === selectedSlug) ?? organizations[0],
    [organizations, selectedSlug],
  )

  // Fires at most once per mount: a repaired cookie can only be repaired once,
  // and a browser that refuses the write (cookies blocked) must not be able to
  // drive an endless refresh loop.
  const staleCookieRepaired = useRef(false)

  // If the resolved slug diverges from the cookie (e.g. cookie pointed at an
  // org the user no longer has access to, so resolveOrgSlug fell back), rewrite
  // the cookie. gpFetch, clientFetch, and middleware read the cookie directly
  // for the X-Organization-Slug header, so a stale cookie would make the API
  // see the wrong slug while the UI shows the fallback.
  //
  // Because `resolveOrgSlug` returns the cookie untouched whenever it is valid,
  // a divergence here means exactly one thing: the cookie named no org this
  // user can see. The server component tree that produced the current page ran
  // against that same unusable cookie, so its data is answering for the wrong
  // org (or for none) while this client tree has already moved on to the right
  // one — the split that renders campaign-manager content beneath a Serve
  // sidebar after an impersonation hand-off. Refreshing re-runs those server
  // components against the repaired cookie so both halves agree.
  //
  // `router.refresh()` deliberately, not a redirect: it re-runs the route the
  // user is already on and preserves client state. No route decision is made or
  // second-guessed here, so this cannot introduce a redirect cycle with the
  // gates that own those decisions (serveAccess, candidateAccess).
  useEffect(() => {
    const resolved = selectedOrganization?.slug
    if (!resolved) return
    if (resolved === getCookie(ORG_SLUG_COOKIE)) return

    setCookie(ORG_SLUG_COOKIE, resolved)

    if (staleCookieRepaired.current) return
    staleCookieRepaired.current = true
    router.refresh()
    void refreshFlags()
  }, [selectedOrganization?.slug, router, refreshFlags])

  const setSelectedSlug = useCallback(
    (slug: string) => {
      setRawSelectedSlug(slug)
      setCookie(ORG_SLUG_COOKIE, slug)
      void refreshFlags()
      // Exclude the organizations and eligibility queries from invalidation —
      // neither changes when switching between orgs (the org list doesn't
      // change, and eligibility is per-user). Invalidating the org list also
      // causes a brief flash where nav items disappear while it refetches.
      //
      // outreach-detail is excluded too, and for a different reason: it's
      // per-row-id, not per-org, so a query for a row owned by the org we're
      // leaving can still be actively observed (e.g. the history table's "N
      // platforms" metric for a just-saved row) at the moment we switch. The
      // cookie above already points at the new org, so invalidateQueries'
      // default `refetchType: 'active'` would immediately refetch that old
      // row's detail endpoint under the NEW org's header, 404ing (ENG-10991).
      // Row ids never repeat across orgs, so the new org's own rows never
      // need this cache entry refreshed either — there's nothing to gain by
      // invalidating it here. contact-notes is the same shape (per-person-id,
      // observed by PhoneBankingNotes while the caller panel is open), so it
      // gets the same exclusion.
      void queryClient.invalidateQueries({
        predicate: (query) =>
          query.queryKey[0] !== ORGANIZATIONS_QUERY_KEY[0] &&
          query.queryKey[0] !== ELIGIBILITY_QUERY_KEY[0] &&
          query.queryKey[0] !== outreachDetailQueryPrefix[0] &&
          query.queryKey[0] !== CONTACT_NOTES_QUERY_PREFIX,
      })
    },
    [queryClient, refreshFlags],
  )

  const contextValue = useMemo(
    () => ({
      organizations,
      selected: selectedOrganization,
      setSelectedSlug,
    }),
    [organizations, selectedOrganization, setSelectedSlug],
  )

  return (
    <OrganizationContext.Provider value={contextValue}>
      {children}
    </OrganizationContext.Provider>
  )
}

export const OrganizationPicker = () => {
  const ctx = useContext(OrganizationContext)
  const router = useRouter()

  const isMobile = useIsMobile()

  const [campaign] = useCampaign()
  const pathname = usePathname()

  const { data: eligibility } = useQuery<Eligibility>({
    queryKey: ELIGIBILITY_QUERY_KEY,
    queryFn: async () =>
      clientRequest('GET /v1/eligibility', {}).then((res) => res.data),
  })

  if (!ctx || ctx.organizations.length === 0 || !ctx.selected) return null

  const { organizations, selected, setSelectedSlug } = ctx

  const handleOrgSwitch = (org: Organization) => {
    if (org.slug === selected.slug) return
    trackEvent(EVENTS.OrgSwitcher.OrganizationSwitched, {
      toStatus: org.status,
      isElectedOfficeOrg: Boolean(org.electedOfficeId),
    })
    // Team accounts (ENG-10816): scoped to the DESTINATION org's role only —
    // not "does the viewer have a non-owner role anywhere" (delegate review,
    // PR #1688). That broader check fired for every switch an owner made
    // between their own owned orgs, as long as they were a manager on some
    // unrelated third org, which isn't what this event is meant to measure.
    if (org.role && org.role !== 'owner') {
      trackEvent(EVENTS.Team.CampaignSwitched)
    }
    setSelectedSlug(org.slug)

    const isOnSharedPage = SHARED_PATHS.some((p) => pathname?.startsWith(p))
    if (!isOnSharedPage) {
      // Volunteer route group (ENG-11052): a switch onto an org where the
      // viewer is a volunteer lands on the reductive /volunteer shell
      // instead of the campaign dashboard.
      router.push(
        org.role === 'volunteer'
          ? '/volunteer'
          : org.electedOfficeId
            ? '/dashboard/chief-of-staff'
            : '/dashboard',
      )
    }
  }

  // Server-side enforcement is the real gate (POST /v1/campaigns/follow-on
  // re-checks eligibility); this navigation only opens the follow-on flow.
  // Task 11 owns the destination screen — for now it reads intent (and the
  // office to inherit, for re-election) off the query string.
  const handleRunFor = (
    intent: 'same-office' | 'new-office',
    fromOrganizationSlug?: string,
  ) => {
    trackEvent(EVENTS.OrgSwitcher.RunForOfficeClicked, {
      intent,
      ...(fromOrganizationSlug ? { fromOrganizationSlug } : {}),
    })
    const params = new URLSearchParams({ intent })
    if (fromOrganizationSlug) params.set('from', fromOrganizationSlug)
    router.push(`/onboarding/office-selection?${params.toString()}`)
  }

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton
              size="lg"
              className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
            >
              <Logo />
              <div className="grid flex-1 text-left text-sm leading-tight">
                <div className="flex items-center gap-1">
                  <p className="truncate text-sm font-opensans">
                    GoodParty.org
                  </p>
                  {campaign?.isPro && <ProBadge size="small" />}
                </div>
                <span className="truncate text-sm font-opensans font-semibold">
                  {selected.name}
                </span>
              </div>
              <ChevronDown className="ml-auto self-end" />
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className="min-w-(--radix-dropdown-menu-trigger-width) rounded-lg"
            align="start"
            side={isMobile ? 'bottom' : 'right'}
            sideOffset={4}
          >
            <DropdownMenuGroup>
              {organizations.map((org) => {
                const isSelected = org.slug === selected.slug
                const isPast = org.status === 'past'
                const office =
                  org.customPositionName ??
                  org.positionName ??
                  org.position?.name
                const secondaryLine = [org.ownerName, office]
                  .filter(Boolean)
                  .join(' · ')
                return (
                  <DropdownMenuItem
                    key={org.slug}
                    onClick={() => handleOrgSwitch(org)}
                    className="gap-2 px-2 py-2.5"
                  >
                    <span
                      className={`flex size-4 shrink-0 items-center justify-center rounded-full border ${
                        isSelected
                          ? 'border-primary bg-primary'
                          : 'border-muted-foreground'
                      }`}
                    >
                      {isSelected && (
                        <span className="size-1.5 rounded-full bg-white" />
                      )}
                    </span>
                    <div className="flex min-w-0 flex-1 flex-col">
                      <span
                        data-testid="org-picker-item-name"
                        className={`truncate text-sm font-opensans ${
                          isPast ? 'text-muted-foreground' : ''
                        }`}
                      >
                        {org.name}
                      </span>
                      {secondaryLine && (
                        <span
                          data-testid="org-secondary-line"
                          className="truncate text-xs font-opensans text-muted-foreground"
                        >
                          {secondaryLine}
                        </span>
                      )}
                    </div>
                    {isPast && (
                      <span className="ml-auto text-xs font-opensans text-muted-foreground">
                        Past
                      </span>
                    )}
                  </DropdownMenuItem>
                )
              })}
            </DropdownMenuGroup>
            {eligibility?.canStartCampaign && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuGroup>
                  {eligibility.reelectionOfficeSlug && (
                    <DropdownMenuItem
                      onClick={() =>
                        handleRunFor(
                          'same-office',
                          eligibility.reelectionOfficeSlug ?? undefined,
                        )
                      }
                      className="gap-2 px-2 py-2.5"
                    >
                      <span className="text-sm font-opensans">
                        Run for re-election
                      </span>
                      <ArrowRightIcon className="ml-auto" />
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem
                    onClick={() => handleRunFor('new-office')}
                    className="gap-2 px-2 py-2.5"
                  >
                    <span className="text-sm font-opensans">
                      Run for a new office
                    </span>
                    <ArrowRightIcon className="ml-auto" />
                  </DropdownMenuItem>
                </DropdownMenuGroup>
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    </SidebarMenu>
  )
}

const Logo = () => (
  <div className="w-[32px] h-[24px]">
    <svg
      width={32}
      height={24}
      viewBox="0 0 32 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M16.0021 21.306C21.2331 18.7631 24.7057 15.9097 26.4789 12.9823C27.9714 10.4985 28.1635 8.08866 27.2178 6.09274C26.3607 4.28903 24.6318 3.00278 22.6222 2.66274C20.4795 2.30791 18.3073 3.06192 16.5932 4.93955L15.9873 5.59006L15.3815 4.93955C13.6526 3.06192 11.4804 2.30791 9.3525 2.66274C7.35762 2.98799 5.61395 4.28903 4.75689 6.09274C3.81117 8.07387 4.00327 10.4985 5.49574 12.9823C7.26896 15.9097 10.7415 18.7631 15.9726 21.306H16.0021Z"
        fill="white"
      />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M16.0022 4.14121C14.0959 2.1453 11.643 1.2878 9.2048 1.70176C6.88482 2.08616 4.87517 3.5794 3.87034 5.6788C2.76207 8.02954 3.02805 10.809 4.66828 13.4998C6.5745 16.6637 10.254 19.6206 15.5736 22.1931L16.0022 22.4001L16.4307 22.1931C21.7504 19.6058 25.415 16.6489 27.336 13.4998C28.9615 10.809 29.2423 8.02954 28.134 5.6788C27.1292 3.56462 25.1195 2.08616 22.7995 1.70176C20.3613 1.30258 17.9084 2.1453 16.0022 4.14121ZM14.6574 5.63445C13.1354 3.97859 11.2883 3.37242 9.5151 3.66811C7.83053 3.94901 6.36762 5.04306 5.65833 6.5363C4.88993 8.1626 4.99337 10.2177 6.35284 12.4797C7.93397 15.0965 11.0815 17.7578 15.9874 20.1972C20.8933 17.7578 24.026 15.0965 25.6219 12.4797C26.9962 10.2177 27.0848 8.1626 26.3164 6.5363C25.6071 5.02828 24.1442 3.94901 22.4597 3.66811C20.6864 3.37242 18.8393 3.97859 17.3173 5.63445L15.9874 7.08333L14.6574 5.63445Z"
        fill="#DC1438"
      />
      <path
        d="M15.4701 14.1653L14.0515 14.9045C13.9037 14.9785 13.7264 14.9193 13.6525 14.7863C13.623 14.7271 13.6082 14.668 13.623 14.5941L13.889 13.0417C13.9481 12.6721 13.8298 12.3025 13.5639 12.0511L12.4113 10.9423C12.293 10.824 12.293 10.6466 12.4113 10.5283C12.4556 10.484 12.5147 10.4544 12.5886 10.4396L14.1845 10.2178C14.5539 10.1587 14.879 9.93693 15.0415 9.59689L15.7508 8.17758C15.8247 8.02973 16.002 7.9706 16.1498 8.04452C16.2089 8.07409 16.2532 8.11844 16.2828 8.17758L16.9921 9.59689C17.1546 9.92215 17.4797 10.1587 17.8491 10.2178L19.4451 10.4396C19.6076 10.4692 19.7258 10.617 19.6963 10.7649C19.6963 10.824 19.6519 10.8831 19.6076 10.9275L18.455 12.0363C18.189 12.2877 18.056 12.6721 18.1299 13.0269L18.3959 14.5793C18.4255 14.7419 18.322 14.8898 18.1595 14.9193C18.1004 14.9193 18.0265 14.9193 17.9674 14.8898L16.5488 14.1505C16.2089 13.9731 15.8099 13.9731 15.4849 14.1505L15.4701 14.1653Z"
        fill="#0048C2"
      />
    </svg>
  </div>
)
